<template>
  <div
    class="cpt-friend-wrapper py-[2px] w-full"
    :class="[
      type ? 'friend-wrapper-' + type : '', 
      {
        'is-system-msg': isSystemMsg,
      }
    ]"
    :data-delegate-open-user-card="friend.user_id"
    :data-delegate-open-user-menu="friend.user_id"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div
      class="cpt-friend-item h-[46px] pr-[4px] pl-[8px] flex-items-center pointer"
      :class="{
        active,
        'no-hover': disableHover,
        'half-transparent': isTranslucent,
        member: isMemberItem,
        'start': isAnimation
      }"
      v-delegate="{
        click: {
          'complete-btn': {
            handler: completeChat
          },
          'icon-chat': {
            handler: jumpPrivateChat
          },
          'invite-btn': {
            handler: inviteFriend
          },
          'icon-room': {
            handler: handleJoinChannel
          },
          'icon-locate': {
            handler: locateUserInRoom
          }
        }
      }">
      <Avatar
        :img="friend.avatar"
        :hideOnline="hideOnline"
        :onlineState="onlineState"
        :unread_count="avatar_unread && friend.new_msg_count"
        :show_unread_dot="friend.show_notify_tag"
        @mouseenter.native="$emit('mouseenter:avatar', $event, 'friend', friend, friend.user_id)"
        @mouseleave.native="$emit('mouseleave:avatar', friend)"
        :avatar_decoration="(isMe ? user_info.avatar_decoration : friendData.avatar_decoration) || {}"
        :no_lazy="true"
        :border-color="useThemeBorderColor && show_theme_sidebar_border_color"
        :hover_dom_handler="() => $el"
      />
      <div class="userinfo flex-grow-1 ml-[10px]">
        <div class="flex-items-center">
          <div 
            class="username leading-[18px] text-[13px] text-op-6 inline-flex items-center max-w-full"
            :class="{
              'is-master': friend.is_master,
              'is-me': isMe,
            }"
            :style="{
              'color': name_color && name_color.includes('linear-gradient') ? '' : name_color,
            }"
          >
            <span
              v-emoji-html="{text: replaceAngleBracket(calcUserName(friend)), className: 'w-[13px]'}"
              class="block ellipsis"
              :class="{
                'bg-gradient': name_color && name_color.includes('linear-gradient'),
              }"
              :style="{
                'background-image': name_color && name_color.includes('linear-gradient') ? name_color : '',
              }"
            ></span>
            <svg v-if="isSystemMsg" t="1691398862921" class="ml-[5px]" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4612" width="14" height="14"><path d="M447.901583 17.114463a127.627084 127.627084 0 0 1 127.627084 0L908.383027 209.176768c39.494914 22.819284 63.850111 64.947192 63.850111 110.512621v384.417165c0 45.565429-24.355197 87.693337-63.850111 110.439482L575.601805 1006.900896a127.627084 127.627084 0 0 1-127.627084 0l-332.781221-192.135443A127.627084 127.627084 0 0 1 51.197111 704.179693V319.762528c0-45.565429 24.282058-87.693337 63.776973-110.439483L447.901583 17.041324z" fill="var(--brand-fill)" p-id="4613"></path><path d="M457.336479 701.985531a21.063954 21.063954 0 0 0 29.694324 0l276.098706-274.270238a21.063954 21.063954 0 0 0 0-29.840602l-51.709082-51.416527a21.063954 21.063954 0 0 0-29.694324 0L487.030803 539.910105a21.063954 21.063954 0 0 1-29.694324 0L354.430286 437.66216a21.063954 21.063954 0 0 0-29.694325 0l-51.709082 51.343389a21.063954 21.063954 0 0 0 0 29.986879l184.3096 182.993103z" fill="#FFFFFF" p-id="4614"></path></svg>
            <MedalIcon                 
              v-for="medal in friend.medals" 
              :key="medal.id"
              :medal="medal"
            ></MedalIcon>
            <p v-if="friend.is_master" class="master-tag flex-center"><span>房主</span></p>
            <p
              v-if="friend.tag && friend.tag.text"
              class="master-tag flex-center custom-tag"
              :style="{
                background: friend.tag.bg_color,
                color: friend.tag.text_color
              }"
            >
              <span>{{friend.tag.text}}</span>
            </p>
            <UserSpark :spark="{ continuous_days: friend.continuous_days }" v-if="showSpark" />
          </div>
        </div>

        <!-- 最近消息 -->
        <div v-if="recent && friendData.last_msg" class="user-status-wrapper mt-[2px] h-[16px]">
          <p class="user-status ellipsis member recent-msg" v-html="getLastMsgHtml()"></p>
        </div>
        <div class="user-status-wrapper mt-[2px] h-[16px]" v-else-if="friendOnlineDesc">
          <p 
            class="user-status ellipsis" 
            :class="{
              member: isMemberItem,
              offline: !onlineState
            }"
            v-html="purifyHTML(friendOnlineDesc)"
          ></p>
        </div>
      </div>
      <div class="time-wrapper" v-if="recent">
        <div
          class="format-time text-op-3 text-[12px] relative top-[-9px] scale-[0.9166] origin-right"
          v-if="friend.update_time"
        >{{ friend.update_time | actualTime }}</div>
        <div class="notify-tag w-[12px] h-[12px] absolute bottom-[-6px] right-0" v-if="friend.show_notify_tag">
          <i class="iconfont icon-notice-off-bold !text-[12px] text-op-3"></i>
        </div>
        <i
          v-if="!isPostMsg"
          v-tooltip="{ text: '不显示' }"
          data-delegate-complete-btn
          class="complete-btn iconfont icon-correct-bold icon-btn hover-btn ml-[4px]"
          @click.stop="completeChat()"
        ></i>
      </div>
      <div class="button-wrapper flex-items-center" v-else-if="!unread && !isMe && !hiddenBtns">
        <i
          v-if="showJumpAudioChannel"
          v-tooltip="{ text: '定位成员所在语音频道' }"
          :data-delegate-icon-locate="friend.user_id"
          class="icon-follow-bold iconfont icon-btn hover-btn"
          @dblclick.stop
        ></i>
        <!-- 离线在线都展示私信按钮 -->
        <template v-if="isFriendsPage">
          <i
            v-if="friendData.active_audio && friendData.active_audio_channel"
            v-tooltip="{ text: '跟随好友前往频道' }"
            :data-delegate-icon-room="friend.user_id"
            class="icon-follow-bold iconfont icon-btn hover-btn"
            @dblclick.stop
          ></i>
          <i
            v-tooltip="{ text: '更多' }"
            :data-delegate-open-user-menu="friend.user_id"
            class="icon-more iconfont icon-btn mr-[10px]"
            @dblclick.stop
          ></i>
          <div
            class="flex-center h-[30px] w-[68px] bg-op-1 px-[10px] rounded-[4px] pointer text-op-5 text-[12px] leading-[16px] pointer"
            :data-delegate-icon-chat="friend.user_id"
            @dblclick.stop
          >
            发送消息
          </div>
          <div
            v-if="(hasJoinRoom || hasJoinAudioChannel) && !sameChannel"
            class="invite-btn flex-center hidden h-[30px] w-[92px] rounded-[4px] bg-bd-fl relative mx-[4px]"
            :class="{
              disable: inviteFrozen
            }"
            data-delegate-invite-btn
          >
            <p class="w-full text-center text-wh-0 text-[12px] leading-[16px]">{{inviteFrozen ? `${inviteFrozenTime - inviteTime}s` : '邀请进入频道'}}</p>
          </div>
        </template>
        <i
          v-else
          v-tooltip="{ text: '发送消息', pointer: 'right' }"
          :data-delegate-icon-chat="friend.user_id"
          class="icon-chat iconfont icon-btn hover-btn"
          @dblclick.stop
        ></i>
        <i
          v-if="friend.room_id && showFollowIcon && !sameChannel"
          v-tooltip="{ text: `跟随好友前往频道`, pointer: 'right' }"
          data-delegate-icon-room
          class="icon-follow-bold iconfont icon-btn hover-btn"
          @dblclick.stop
        ></i>
        <div
          v-if="(hasJoinRoom || hasJoinAudioChannel) && !sameChannel && !isFriendsPage"
          v-tooltip="{ text: inviteText, pointer: 'right', disable: inviteFrozen || !inviteText }"
          class="invite-btn hover-btn hidden h-[22px] w-[37px] rounded-[3px] bg-bd-fl relative mx-[4px]"
          :class="{
            disable: inviteFrozen
          }"
          data-delegate-invite-btn
        >
          <p class="w-full text-center text-wh-0 text-[11px] leading-[22px]">{{inviteFrozen ? `${inviteFrozenTime - inviteTime}s` : '邀请'}}</p>
        </div>
        <i
          v-if="friend.show_join_room && !$route.name === 'room'"
          v-tooltip="{ text: '跟随进房', pointer: 'right' }"
          class="hover-btn icon-btn iconfont icon-room"
          data-delegate-icon-chat
        ></i>
      </div>
      <div class="button-wrapper flex-items-center" v-else-if="isMe && !unread">
        <div class="mr-[8px] text-[12px] text-op-3">我</div>
      </div>
      <slot></slot>
      <div
        class="unread-count"
        v-if="unread && friend.new_msg_count > 0"
        :style="{
          'border-color': show_theme_data.bar_color ? show_theme_data.bar_color[0] : '',
        }"
      >
        <div class="unread-count-text">{{ friend.new_msg_count }}</div>
      </div>
    </div>
  </div>

</template>

<script>
import { mapGetters } from "vuex";
import { checkLoginStatusAsync, IMTimeFormat } from "_lib/utils/utils";
import { NO_PERMISSION_TYPE } from '_lib/constants/no-permission';
import EventBus from '_lib/EventBus';
import Avatar from '_com/func/Avatar';
import { NEW_OFFICIAL_MESSAGE_ID, RECENT_MESSAGE_ENTRY } from '@/common/constants/message';
import { reportData } from '_api/report';
import { calcUserName, calcUserRoleColor } from '_lib/CalcUserInfo'
import { decimalToHexColor } from "_lib/utils/utilsColor";
import { purifyHTML, replaceAngleBracket } from "_lib/purify";
import { getShowSparkUserMinDay } from "_lib/constants/spark";
import UserSpark from "_com/func/UserSpark.vue";
const MedalIcon = () => import('_com/func/MedalIcon.vue')
import markdownToHtml from '_lib/MarkdownToHtml';
import { ROOM_NOTIFY_SETTING_ENUM } from "_lib/constants/room";

export default {
  props: {
    friend: {
      default: () => {}
    },
    room_name: {
      default: ""
    },
    active: {
      default: false
    },
    unread: {
      default: false
    },
    avatar_unread: {
      default: false
    },
    disableHover: {
      default: false
    },
    hideOnline: {
      default: false
    },
    static: {
      default: false
    },
    color: {
      default: ''
    },
    type: {
      default: ''
    },
    hiddenBtns: {
      default: false
    },
    recent: {
      default: false
    },
    no_avatar_lazy: {
      default: false
    },
    useThemeBorderColor: {
      default: true
    },
    isRoomSearch: {
      default: false
    },
    needShowUserSpark: {
      type: Boolean,
      default: false,
    },
    inviteTime: {
      default: 0
    },
    showFollowIcon: {
      type: Boolean,
      default: false
    },
    isFriendsPage: {
      type: Boolean,
      default: false
    },
    ignoreOnlineState: {
      type: Boolean,
      default: false
    },
  },
  watch: {
    inviteTime: {
      handler(v) {
        if(this.inviteFrozen && this.inviteFrozenTime - v <= 0) {
          this.inviteFrozen = false
        }
      },
    }
  },
  data() {
    return {
      isAnimation: false,
      calcUserName,
      inviteFrozen: false,
      inviteFrozenTime: 0,
      showJumpAudioChannel: '',
      highlightChannelId: '',
    };
  },
  components: {
    Avatar,
    MedalIcon,
    UserSpark,
  },
  computed: {
    hasJoinRoom() {
      return this.cur_room_data && this.cur_room_data.room_id;
    },
    hasJoinAudioChannel() {
      return this.cur_channel_data.channel_id;
    },
    cur_real_channel () {
      return (this.$route.name === 'room') && this.cur_text_channel_data.channel_id ? this.cur_text_channel_data : this.cur_channel_data
    },
    sameChannel() {
      return this.cur_real_channel.channel_id && this.friend.channel_id && this.cur_real_channel.channel_id == this.friend.channel_id
    },
    inviteText () {
      if (this.friend.bot) {
        return '邀请至其他房间'
      }
      if (!this.cur_real_channel.channel_id && this.hasJoinRoom) {
        return '邀请进入房间'
      } 
      if (this.cur_real_channel.channel_id) {
        return '邀请进入频道'
      }
      return ''
    },
    friendData() {
      // MainLayout里面游戏识别更新会导致user_state不带game_icon，如果后续需要展示game_icon这边就要给游戏识别的后端接口加上game_icon
      if(this.static) return this.friend
      return this.friends_map[this.friend.user_id] || this.friend
    },
    isMe() {
      return this.user_info.user_id == this.friend.user_id
    },
    onlineState() {
      if (this.isMe) {
        return this.online_state
      }
      return Number(this.friendData.state || this.friendData.online_state)
    },
    isTranslucent() {
      if(this.ignoreOnlineState) return false
      if (this.isMe) {
        return this.isMemberItem && !this.online_state
      }
      return this.isMemberItem && !(this.friend.state || this.friend.online_state)
    },
    name_color() {
      if(this.type != 'member' || !this.friend.roles) return
      const color = this.color ? decimalToHexColor(this.color) : calcUserRoleColor(this.friend.roles)
      if (Array.isArray(color) && color.length === 2) {
        return `linear-gradient(90deg, ${color[0]} 0%, ${color[1]} 100%)`
      }
      return color
    },
    isMemberItem() {
      return this.type == 'member'
    },
    friendOnlineDesc() {
      if (this.hideOnline) {
        return ''
      } else if (this.friendData.active_audio && this.friendData.active_audio_channel && this.type !== 'member') {
        const active_audio_channel = this.friendData.active_audio_channel || {}
        return `语音中: ${active_audio_channel.channel_name} / ${active_audio_channel.room_name}`
      } else if (this.friendData.user_state 
        && (this.friendData.user_state.game_name 
        || this.friendData.user_state.music_name
        || this.friendData.user_state.steam_game_name
        || this.friendData.user_state.web_game_name)
      ) {
        if (this.friendData.user_state.steam_game_name) {
          let str = '游戏中: ' + this.friendData.user_state.steam_game_name
          if (this.friendData.user_state.steam_game_state) {
            str += ` <span class="game-state"> ${this.friendData.user_state.steam_game_state}</span>`
          }
          return str
        }
        if (this.friendData.user_state.game_name || this.friendData.user_state.web_game_name) {
          return '游戏中: ' + (this.friendData.user_state.game_name || this.friendData.user_state.web_game_name)
        }
        if (this.friendData.user_state.music_name) {
          return '听歌中: ' + this.friendData.user_state.music_name
        }
      } else if (!this.onlineState && !this.hideOnline) {
        if (this.friendData.logout_time && this.getTimeAgo(this.friendData.logout_time)) {
          return this.getTimeAgo(this.friendData.logout_time) + '在线'
        }
      }
      return ''
    },
    isSystemMsg() {
      return this.isPostMsg || this.isOfficialMsg || this.isRequestMsg
    },
    isPostMsg() {
      return this.friendData.user_id === NEW_OFFICIAL_MESSAGE_ID.heychat_interaction_msg
    },
    isOfficialMsg () {
      return this.friendData.user_id === NEW_OFFICIAL_MESSAGE_ID.heychat_sys_notify
    },
    isRequestMsg () {
      return this.friendData.user_id === NEW_OFFICIAL_MESSAGE_ID.heybox_message_notify
    },
    showSpark () {
      if(!this.needShowUserSpark) return false
      if(!!this.cur_room_data.is_public) return false
      return !this.cur_room_data.room_setting?.hide_member_fire_flag && !this.friend.hide_fire_flag && this.friend.continuous_days >= getShowSparkUserMinDay()
    },
    ...mapGetters([
      "cur_room_data", 
      "channel_data",
      'cur_channel_data',
      'cur_text_channel_data',
      "friends_map",
      "user_info", 
      'online_state',
      "show_theme_sidebar_border_color",
      'active_sidebar_tab',
      'show_theme_data',
      'all_chat_group_notify_settings',
      'cur_group_chat'
    ]),
  },
  methods: {
    completeChat(e) {
      if (!this.isSystemMsg) {
        const sendData = { ...this.friend, from: 'SIDEBAR' };
        EventBus.$emit('Hide_Private_IM', sendData);
        setTimeout(() => {
          this.$emit("complete_chat", this.friend);
          // 动画完成后，可以将isAnimation设置回false，如果需要的话
        });
      } else {
        EventBus.$emit('RecentMsg:Complete_Chat', this.friend);
      }
    },
    startAnimation() {
      this.isAnimation = true;
    },
    async jumpPrivateChat(event) {
      if(!await checkLoginStatusAsync()){
        EventBus.$dynamic('Login')
        return
      }
      event._currentTarget = event.currentTarget
      // if (this.isFriendsPage) {
      // } else {
      //   EventBus.$emit('Jump_Private_Chat', this.friend)
      // }
      EventBus.$emit('Show_Private_IM', this.friend)
      EventBus.$emit('User_Menu:card', false)
    },
    showUnreadUserCard(event) {
      if (!this.unread || this.isOfficialMsg || this.isRequestMsg) {
        return;
      }
      event.stopPropagation();
      this.$emit('show-user-card', event);
    },
    async inviteFriend(event) {
      event.stopPropagation();
      if(!await checkLoginStatusAsync()){
        EventBus.$dynamic('Login')
        return
      }
      if (this.inviteFrozen) return 
      if (this.friend.bot) {
        this.inviteFrozen = false
        EventBus.$dynamic('BotInviteDialog', { botId: this.friend.user_id })
        return
      }
      if (!this.$permission.judge('CreateInvitation', this.cur_real_channel)) {
        this.$toast.no_permission(NO_PERMISSION_TYPE.ROOM_NO_ROLE)
        return
      }

      this.$emit('inviteFriend', this.friend.user_id, true)
      reportData('invite:copy-link', {
        pos: this.isMemberItem ? 'member-hover' : 'friend-hover'
      })
      this.inviteFrozen = true
      this.inviteFrozenTime = this.inviteTime + 60
    },
    handleJoinChannel(event) {
      event.stopPropagation();
      this.$emit('joinChannel', this.friend)
    },
    locateUserInRoom(event) {
      event.stopPropagation();
      EventBus.$emit('Highlight_Channel_User', this.showJumpAudioChannel, this.friend.user_id)
    },
    getTimeAgo(timestamp) {
      const now = new Date().getTime();
      const diff = now - timestamp;

      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));

      if (minutes <= 0) {
        return '刚刚';
      } else if (minutes < 60) {
        return minutes + '分钟前';
      } else if (hours < 5) {
        return hours + '小时前';
      } else if (hours < 24) {
        const date = new Date(timestamp);
        const today = new Date(now);
        const yesterday = new Date(now - 24 * 60 * 60 * 1000);

        if (
          date.getDate() === today.getDate() &&
          date.getMonth() === today.getMonth() &&
          date.getFullYear() === today.getFullYear()
        ) {
          return '今日';
        } else if (
          date.getDate() === yesterday.getDate() &&
          date.getMonth() === yesterday.getMonth() &&
          date.getFullYear() === yesterday.getFullYear()
        ) {
          return '昨日';
        }
      }
      return '';
    },
    handleMouseEnter () {
      if (this.isRoomSearch) {
        this.$emit('set-active', this.friend.user_id)
      }
      if (this.recent || this.isMe || this.hiddenBtns || !this.onlineState) return
      if ((this.active_sidebar_tab !== 'room') || !this.isRoomSearch) return
      this.showJumpAudioChannel = this.friend.cur_audio_channel_id || ''
    },
    handleMouseLeave () {
      if (this.recent || this.isMe || this.hiddenBtns) return
      if (this.active_sidebar_tab !== 'room' || !this.isRoomSearch) return
      this.showJumpAudioChannel = ''
    },
    getLastMsgHtml () {
      const msgContent = purifyHTML(markdownToHtml(this.friendData.last_msg?.replace(/<br>/g, '').replace(/br/g, '') || ''))
      const isChatGroup = this.friendData.entry === RECENT_MESSAGE_ENTRY.chat_group
      if(!isChatGroup) {
        return msgContent
      }

      const msgPrefixObj = this.friendData.msg_prefix

      // 有消息前缀
      if(msgPrefixObj && msgPrefixObj.text) {
        const { pc_color, text, show_notify_setting } = msgPrefixObj
        const currentGroupNotifySetting = this.all_chat_group_notify_settings[this.friendData.chat_group_info.chat_group_id] ?? ROOM_NOTIFY_SETTING_ENUM.extend
        const isNeedToShow = currentGroupNotifySetting <= show_notify_setting
        if(isNeedToShow) {
          return `<span style='color: ${pc_color}'>${text}</span> ${msgContent}`
        }
      }
      
      // 有未读消息
      if(this.friendData.new_msg_count > 0) {
        return `<span>[${this.friendData.new_msg_count > 99 ? '99+' : this.friendData.new_msg_count}条]</span> ${msgContent}`
      }

      // 没有消息前缀和未读消息
      return msgContent
    },
    replaceAngleBracket,
    purifyHTML,
  },
  filters: {
    actualTime (timestamp) {
      return IMTimeFormat(timestamp, false)
    }
  },
};
</script>

<style lang="scss">
.cpt-friend-wrapper {
  &:not(.is-system-msg):hover {
    .recent-msg {
      width: 346px !important;
    }
    .format-time {
      display: none !important;
    }
    .notify-tag {
      display: none !important;
    }
    .complete-btn {
      display: inline-flex !important;
    }
  }
}
.cpt-friend-item {
  &.start {
    animation: slide .2s linear forwards;
  }
  &.member {
    height: 40px;
  }
  &.half-transparent {
    opacity: 0.4;
    .username {
      color: $d-tx-2;
    }
  }
  &.no-hover {
    &:hover, &.active {
      background: none;
    }
  }
  
  &:hover, &.active {
    background: $d-op-1;
    border-radius: 5px;
    .hover-btn:not(.complete-btn) {
      display: inline-flex !important;
      &.disable {
        opacity: .4;
        cursor: not-allowed;
      }
    }
  }
  .hover-btn {
    display: none;
  }
  .userinfo {
    .username {
      &.is-master, &.is-me {
        max-width: calc(100% - 4px)
      }
    }
    .master-tag {
      flex-shrink: 0;
      width: 27px;
      height: 14px;
      margin-left: 5px;
      border-radius: 3px;
      background: $d-wn-fl;
      color: $ct-wh-0;
      font-weight: 400;
      letter-spacing: 0.1px;
      &.custom-tag {
        width: auto;
      }
      span {
        font-size: 12px;
        transform: scale(.8334);
      }
    }
    .user-status-wrapper {
      .user-status {
        height: 32px;
        width: 200%;
        line-height: 32px;
        font-size: 22px;
        color: $d-ol-tx;
        transform: scale(.5);
        transform-origin: left top;
        &.recent-msg {
          width: 400px;
          br {
            display: none;
          }
          * {
            display: inline !important;
            pointer-events: none !important;
            color: $d-op-4;
          }
        }
        &.member {
          color: $d-op-4 !important;
        }
        &.offline {
          color: $d-op-3;
        }
        .game-state {
          opacity: .7;
        }
      }
    }
  }

  .time-wrapper {
    position: relative;
    .complete-btn {
      color: $d-op-4;
      margin-right: 5px;
      display: none;
      &:hover {
        color:  $d-bd-tx;
      }
    }
  }
  .button-wrapper {
    .icon-btn {
      margin-left: 4px
    }
    .invite-btn {
      &.disable {
        cursor: not-allowed;
        opacity: .4;
      }
    }
  }
  .unread-count {
    height: 32px;
    min-width: 32px;
    margin-right: 4px;
    padding: 0px 8px;
    background: $d-er-fl;
    border-radius: 16px;
    transform: scale(.5);
    transform-origin: right top;
    &-text {
      font-size: 22px;
      font-weight: 500;
      color: $ct-wh-0;
      line-height: 32px;
      text-align: center;
      font-family: 'Roboto';
    }
  }
}
@keyframes slide {
  0% {
    margin-left: 0;
  }
  100% {
    margin-left: 380px;
  }
}
</style>
